import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, timeout } from 'rxjs';
import { SignJWT } from 'jose';
import { Role, UserManagementService, UserWithRelations } from '../../../../@backend';
import { environment } from '../../../../environments/environment';
import {
  CanvasToolkitModalComponent,
  ToolkitAction,
  ToolkitModalInput,
  ToolkitProfile,
  ToolkitRole,
  ToolkitSavePayload
} from '../../../../../projects/canvas-toolkit-modal/src/public-api';
import { LocalUser } from '../../models/localUser';
import { AuthenticationService } from '../../services/authentication.service';
import { JsonModalComponent } from '../json-modal/json-modal.component';

interface ToolkitConfig {
  tokenSecret?: string;
  expirationUnix?: number | null;
  selectedProfileId?: string | number | null;
}

type ToolkitUserRecord = UserWithRelations & {
  ownerIds?: number[];
  allOwners?: boolean;
};

@Component({
  standalone: true,
  imports: [CommonModule, CanvasToolkitModalComponent],
  selector: 'app-toolkit-modal',
  template: `
    <canvas-toolkit-modal
      [config]="modalConfig"
      [extraActions]="extraActions"
      stateStorageKey="apptoolkit.config"
      (saveRequested)="saveCurrentSession($event)"
      (fastLoginRequested)="fastLogin($event)"
      (tokenSecretChange)="tokenSecret = $event"
      (expirationUnixChange)="expirationUnix = $event"
      (selectedRolesChange)="selectedRoles = $event"
      (openExternalRequested)="openURL($event)"
      (jsonRequested)="openJsonDialog($event)"
      (actionRequested)="onAction($event)"
    />
  `
})
export class AppToolkitModalComponent {
  @ViewChild(CanvasToolkitModalComponent) toolkitModal?: CanvasToolkitModalComponent;

  loading = false;
  profilesLoading = false;
  profilesError: string | null = null;
  roles: Role[] = [];
  profiles: ToolkitProfile[] = [];
  selectedRoles: ToolkitRole[] = [];
  tokenSecret = '';
  expirationUnix: number | null = Math.round(Date.now() / 1000) + 7 * 24 * 3600;
  modalConfig: ToolkitModalInput;

  readonly extraActions: ToolkitAction[] = [
    { id: 'reload', label: 'Reload', icon: 'refresh', tooltip: 'Reload the current page' },
    { id: 'clear-toolkit-state', label: 'Reset toolkit', icon: 'restart_alt', tooltip: 'Clear saved toolkit settings' }
  ];

  constructor(
    private readonly dialogRef: MatDialogRef<AppToolkitModalComponent>,
    private readonly dialog: MatDialog,
    public readonly authService: AuthenticationService,
    private readonly userManagementService: UserManagementService
  ) {
    this.restoreToolkitConfig();
    this.selectedRoles = [...(this.authService.currentUser?.roles ?? [])];
    this.modalConfig = this.buildModalConfig();
    void this.loadToolkitData();
  }

  private buildModalConfig(): ToolkitModalInput {
    const hasAuthenticatedSession = this.hasAuthenticatedSession;
    return {
      title: 'Canvas Developer Toolkit',
      loading: this.loading,
      developmentMode: !environment.production,
      user: this.authService.currentUser,
      roles: this.roles,
      selectedRoles: this.selectedRoles,
      expirationUnix: this.expirationUnix,
      tokenSecret: this.tokenSecret,
      explorerUrl: `${environment.baseApiUrl}/explorer`,
      environment: this.getAllEnvironmentVariables(),
      diagnostics: this.getRuntimeDiagnostics(),
      fastLogin: {
        // Passwordless profile switching is deliberately narrower than the
        // rest of the toolkit: it is unavailable on DEV/UAT/prod hosts.
        enabled: environment.isLocal === true && hasAuthenticatedSession,
        profiles: this.profiles,
        loading: this.profilesLoading,
        error: this.profilesError,
        requireConfirmation: true,
        requiresTokenSecret: true,
        buttonLabel: 'Start test session'
      },
      saveButtonLabel: 'Apply session overrides'
    };
  }

  private refreshModalConfig(): void {
    this.modalConfig = this.buildModalConfig();
  }

  private async loadToolkitData(): Promise<void> {
    // Optional development data must never block the rest of the toolkit.
    if (!this.hasAuthenticatedSession) return;
    void this.loadRoles();
    if (environment.isLocal) void this.loadProfiles();
  }

  private get hasAuthenticatedSession(): boolean {
    return this.authService.isLoggedIn === true && !!this.authService.currentUser?.token;
  }

  private async loadRoles(): Promise<void> {
    try {
      this.roles = await firstValueFrom(
        this.userManagementService.userManagementControllerGetRolesForSelect().pipe(timeout(5000))
      );
      this.refreshModalConfig();
    } catch (error) {
      console.error('Unable to load toolkit roles.', error);
    }
  }

  private async loadProfiles(): Promise<void> {
    this.profilesLoading = true;
    this.profilesError = null;
    this.refreshModalConfig();
    try {
      // Fast-login tokens need owner relations, which the select endpoint omits.
      // This request only runs for an existing authenticated session.
      const users = await firstValueFrom(
        this.userManagementService.userManagementControllerGetAllUsers(true, true).pipe(timeout(5000))
      ) as ToolkitUserRecord[];
      this.profiles = users
        .filter((user) => !!user.id && !!user.email && user.active !== false && user.canLogin !== false)
        .map((user) => this.toToolkitProfile(user))
        .sort((a, b) => this.profileName(a).localeCompare(this.profileName(b)));
    } catch (error) {
      this.profilesError = 'Test profiles are unavailable. The rest of the toolkit is still ready.';
      console.error('Unable to load toolkit test profiles.', error);
    } finally {
      this.profilesLoading = false;
      this.refreshModalConfig();
    }
  }

  async saveCurrentSession(payload: ToolkitSavePayload): Promise<void> {
    const current = this.authService.currentUser;
    if (!current) return;
    await this.createDevelopmentSession({ ...current, roles: payload.selectedRoles as Role[] }, payload);
  }

  async fastLogin(profile: ToolkitProfile): Promise<void> {
    if (!environment.isLocal) {
      this.toolkitModal?.resetLoginPending();
      throw new Error('Fast login is only available on a local development build.');
    }

    try {
      await this.createDevelopmentSession(profile, {
        selectedRoles: profile.roles ?? [],
        expirationUnix: this.expirationUnix,
        tokenSecret: this.tokenSecret
      });
    } catch (error) {
      this.toolkitModal?.resetLoginPending();
      console.error('Unable to create the development session.', error);
    }
  }

  onAction(actionId: string): void {
    if (actionId === 'reload') {
      window.location.reload();
      return;
    }
    if (actionId === 'clear-toolkit-state') {
      localStorage.removeItem('apptoolkit.config');
      this.tokenSecret = '';
      this.expirationUnix = Math.round(Date.now() / 1000) + 7 * 24 * 3600;
      this.refreshModalConfig();
    }
  }

  openURL(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openJsonDialog(data: unknown): void {
    this.dialog.open(JsonModalComponent, { data });
  }

  private async createDevelopmentSession(profile: ToolkitProfile | LocalUser, payload: ToolkitSavePayload): Promise<void> {
    if (environment.production || !this.tokenSecret.trim()) {
      throw new Error('A non-production build and token secret are required to create a test session.');
    }

    const claims: LocalUser = {
      id: profile.id == null ? undefined : String(profile.id),
      modxUserId: profile.modxUserId == null ? undefined : String(profile.modxUserId),
      firstname: profile.firstname,
      lastname: profile.lastname,
      email: profile.email == null ? undefined : profile.email,
      roles: payload.selectedRoles as Role[],
      ownerIds: this.readOwnerIds(profile),
      allOwners: profile.allOwners === true
    };

    const secret = new TextEncoder().encode(this.tokenSecret);
    const now = Math.round(Date.now() / 1000);
    const expiration = payload.expirationUnix && payload.expirationUnix > now
      ? payload.expirationUnix
      : now + 7 * 24 * 3600;
    const token = await new SignJWT({ ...claims })
      .setProtectedHeader({ alg: 'HS256', typ: 'JWT' })
      .setIssuedAt(now)
      .setExpirationTime(expiration)
      .sign(secret);

    const localUser: LocalUser = { ...claims, token, iat: now, exp: expiration };
    localStorage.setItem('currentCanvasUser', JSON.stringify(localUser));
    localStorage.removeItem('currentCanvasOwnerId');
    this.dialogRef.close(true);
    window.location.reload();
  }

  private toToolkitProfile(user: ToolkitUserRecord): ToolkitProfile {
    return {
      ...user,
      id: user.id!,
      email: user.email,
      roles: user.roles ?? [],
      ownerIds: user.ownerIds ?? (user.owners ?? []).map((owner) => owner.id).filter((id): id is number => id !== undefined),
      allOwners: user.allOwners === true,
      description: user.category ?? undefined
    };
  }

  private readOwnerIds(profile: ToolkitProfile | LocalUser): number[] {
    if (Array.isArray(profile.ownerIds)) return profile.ownerIds;
    const owners = 'owners' in profile ? profile.owners : undefined;
    return Array.isArray(owners)
      ? owners.map((owner) => Number((owner as { id?: number }).id)).filter((id) => Number.isInteger(id) && id > 0)
      : [];
  }

  private profileName(profile: ToolkitProfile): string {
    return `${profile.firstname ?? ''} ${profile.lastname ?? ''} ${profile.email ?? ''}`.trim();
  }

  private restoreToolkitConfig(): void {
    try {
      const raw = localStorage.getItem('apptoolkit.config');
      if (!raw) return;
      const config = JSON.parse(raw) as ToolkitConfig;
      this.tokenSecret = config.tokenSecret ?? '';
      this.expirationUnix = config.expirationUnix ?? this.expirationUnix;
    } catch {
      localStorage.removeItem('apptoolkit.config');
    }
  }

  private getAllEnvironmentVariables(): Record<string, unknown> {
    return { ...environment };
  }

  private getRuntimeDiagnostics(): Record<string, unknown> {
    return {
      online: navigator.onLine,
      route: window.location.pathname,
      viewport: `${window.innerWidth} × ${window.innerHeight}`,
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator.userAgent
    };
  }
}
